import type { PanelData, PanelDataPoint, TimeProfileData } from "./api";

export type TrafficView = "all" | "miv" | "mv";

export type TrafficViewDefinition = {
  key: TrafficView;
  shortLabel: string;
  label: string;
  includedLabel: string;
  classValues?: ReadonlySet<string>;
};

const mivClassValues = new Set(["car", "pkw", "van", "transporter", "vehicle"]);
const mvClassValues = new Set([...mivClassValues, "truck", "lkw", "bus"]);

const definitions: Record<TrafficView, TrafficViewDefinition> = {
  all: {
    key: "all",
    shortLabel: "Alle",
    label: "Alle Verkehrsteilnehmenden",
    includedLabel: "Alle erfassten Verkehrsmodi",
  },
  miv: {
    key: "miv",
    shortLabel: "MIV",
    label: "Motorisierter Individualverkehr",
    includedLabel: "Pkw, Transporter und Fahrzeug",
    classValues: mivClassValues,
  },
  mv: {
    key: "mv",
    shortLabel: "MV",
    label: "Motorisierter Verkehr",
    includedLabel: "Pkw, Transporter, Fahrzeug, Lkw und Bus",
    classValues: mvClassValues,
  },
};

export function trafficViewDefinition(view: TrafficView) {
  return definitions[view];
}

export function trafficViewClassValues(classValues: string[], view: TrafficView) {
  const included = definitions[view].classValues;
  return included ? classValues.filter((classValue) => included.has(classValue)) : classValues;
}

function filteredPoints(points: PanelDataPoint[], view: Exclude<TrafficView, "all">) {
  const included = definitions[view].classValues!;
  return points.filter((point) => included.has(String(point.groups.class)));
}

function filteredTimeProfile(profile: TimeProfileData, view: Exclude<TrafficView, "all">): TimeProfileData {
  const included = definitions[view].classValues!;
  return { ...profile, rows: profile.rows.filter((row) => included.has(row.class_value)) };
}

export function panelDataForTrafficView(data: PanelData, view: TrafficView): PanelData {
  if (view === "all") return data;
  return {
    ...data,
    points: filteredPoints(data.points, view),
    time_profile: data.time_profile ? filteredTimeProfile(data.time_profile, view) : undefined,
    measurement_points: data.measurement_points?.map((point) => ({ ...point, points: filteredPoints(point.points, view) })),
  };
}

export function trafficViewKnownClassValues(classValues: string[], view: TrafficView) {
  return trafficViewClassValues(classValues, view);
}
